// itemFields — 12종 item 타입별 payload 필드 정의 (단일 출처).
//
// 서버 server/src/services/itemTypes.ts 의 zod 스키마와 키·필수 여부·값 형식을 1:1로 맞춘다.
// 등록 폼(NewItemPanel)·수정 폼(ItemEditForm)·상세 보기(PayloadFields)가 모두 이 정의를 쓴다.
//
// 2026-10-03 — 이전엔 화면마다 다른 키(notes·provider·markdown 등)를 써서
//   필수 키 불일치로 등록 실패(체크리스트·env·인증·메모 등) + 서버가 모르는 키는 조용히 버려지는 문제가 있었다.
//   서버 스키마를 바꾸면 이 파일도 반드시 같이 바꾼다.

import type { ItemType } from '../api/items';

/** 필드 값 형식 — 입력 위젯과 서버 전송 시 변환 방식을 결정 */
export type FieldKind =
  | 'text' // 한 줄 문자열
  | 'secret' // 한 줄 비밀값 (마스킹)
  | 'multiline' // 여러 줄 문자열
  | 'secret-multiline' // 여러 줄 비밀값 (PEM 개인키 등)
  | 'number' // 숫자 → number로 전송
  | 'date' // YYYY-MM-DD 문자열
  | 'select' // options 중 하나
  | 'list' // 쉼표 구분 → string[]로 전송
  | 'boolean'; // 예/아니오 → boolean으로 전송

export interface FieldOption {
  value: string;
  label: string;
}

export interface ItemFieldMeta {
  /** 서버 payload 키 */
  key: string;
  /** 한국어 레이블 */
  label: string;
  kind: FieldKind;
  /** 선택 필드 여부 (서버 스키마 optional) */
  optional?: boolean;
  placeholder?: string;
  /** select 선택지 */
  options?: FieldOption[];
  /** 상세 보기에서 복사 버튼 표시 */
  copyable?: boolean;
}

/** 비밀값 필드 여부 (마스킹·자동완성 차단 대상) */
export function isSecretKind(kind: FieldKind): boolean {
  return kind === 'secret' || kind === 'secret-multiline';
}

/** 여러 줄 입력 여부 */
export function isMultilineKind(kind: FieldKind): boolean {
  return kind === 'multiline' || kind === 'secret-multiline';
}

const memo: ItemFieldMeta = { key: 'memo', label: '메모', kind: 'multiline', optional: true };
const notes: ItemFieldMeta = { key: 'notes', label: '메모', kind: 'multiline', optional: true };

/** 타입별 필드 정의 — 서버 itemTypes.ts 와 동일한 키·필수 여부 */
export const ITEM_FIELDS: Record<ItemType, ItemFieldMeta[]> = {
  credential_account: [
    { key: 'username', label: '사용자명', kind: 'text', copyable: true, placeholder: '아이디 또는 이메일' },
    { key: 'password', label: '비밀번호', kind: 'secret', copyable: true },
    { key: 'url', label: 'URL', kind: 'text', optional: true, copyable: true, placeholder: 'https://example.com' },
    memo,
  ],
  credential_api_key: [
    { key: 'api_key', label: 'API 키', kind: 'secret', copyable: true },
    { key: 'secret_key', label: '시크릿 키', kind: 'secret', optional: true, copyable: true },
    { key: 'endpoint', label: '엔드포인트', kind: 'text', optional: true, copyable: true, placeholder: 'https://api.example.com' },
    memo,
  ],
  credential_server: [
    { key: 'host', label: '호스트', kind: 'text', copyable: true, placeholder: '192.168.0.10 또는 hostname' },
    { key: 'user', label: '사용자', kind: 'text', copyable: true, placeholder: 'root, ubuntu 등' },
    { key: 'port', label: '포트', kind: 'number', optional: true, copyable: true, placeholder: '22' },
    { key: 'password', label: '비밀번호', kind: 'secret', optional: true, copyable: true },
    { key: 'private_key', label: '개인키 (SSH)', kind: 'secret-multiline', optional: true, copyable: true, placeholder: '-----BEGIN OPENSSH PRIVATE KEY-----' },
    memo,
  ],
  credential_env: [
    { key: 'content', label: '내용 (.env)', kind: 'secret-multiline', copyable: true, placeholder: 'KEY=value' },
  ],
  credential_auth: [
    { key: 'api_key', label: 'Client ID / API 키', kind: 'text', copyable: true },
    { key: 'secret', label: 'Client Secret', kind: 'secret', copyable: true },
    memo,
  ],
  note: [
    { key: 'content', label: '내용', kind: 'multiline', placeholder: '메모 내용' },
  ],
  server: [
    { key: 'host', label: '호스트', kind: 'text', copyable: true },
    { key: 'user', label: '사용자', kind: 'text', copyable: true },
    { key: 'port', label: '포트', kind: 'number', optional: true, placeholder: '22' },
    { key: 'roles', label: '역할 (쉼표 구분)', kind: 'list', optional: true, placeholder: 'web, db' },
    { key: 'backup_path', label: '백업 경로', kind: 'text', optional: true, copyable: true },
    notes,
  ],
  domain: [
    { key: 'domain', label: '도메인', kind: 'text', copyable: true, placeholder: 'example.com' },
    { key: 'expires_at', label: '만료일', kind: 'date', optional: true },
    { key: 'registrar', label: '등록기관', kind: 'text', optional: true },
    { key: 'dns_provider', label: 'DNS 제공자', kind: 'text', optional: true },
    { key: 'auto_renew', label: '자동 갱신', kind: 'boolean', optional: true },
    notes,
  ],
  service: [
    { key: 'identifier', label: '식별자', kind: 'text', copyable: true, placeholder: '서비스명·URL·컨테이너명' },
    { key: 'image', label: '이미지', kind: 'text', optional: true, placeholder: 'nginx:1.27' },
    { key: 'compose_path', label: 'compose 경로', kind: 'text', optional: true, copyable: true },
    { key: 'ports', label: '포트 (쉼표 구분)', kind: 'list', optional: true, placeholder: '80, 443' },
    {
      key: 'status', label: '상태', kind: 'select', optional: true,
      options: [
        { value: 'active', label: '운영 중' },
        { value: 'stopped', label: '중지' },
        { value: 'planned', label: '계획' },
      ],
    },
  ],
  subscription: [
    { key: 'category', label: '분류', kind: 'text', placeholder: '예: 클라우드, 도메인, SaaS' },
    { key: 'amount', label: '금액', kind: 'number', placeholder: '0' },
    { key: 'currency', label: '통화', kind: 'text', optional: true, placeholder: 'KRW' },
    {
      key: 'cycle', label: '결제 주기', kind: 'select', optional: true,
      options: [
        { value: 'monthly', label: '매월' },
        { value: 'yearly', label: '매년' },
        { value: 'one_time', label: '1회' },
      ],
    },
    { key: 'next_billing_at', label: '다음 결제일', kind: 'date', optional: true },
    { key: 'payment_method_hint', label: '결제 수단 메모', kind: 'text', optional: true, placeholder: '예: 신한카드 끝자리 1234' },
  ],
  file: [
    { key: 'original_name', label: '파일명', kind: 'text', copyable: true },
    { key: 'mime', label: 'MIME 타입', kind: 'text', placeholder: 'application/pdf' },
    { key: 'size_bytes', label: '크기 (바이트)', kind: 'number', placeholder: '0' },
    { key: 'category', label: '분류', kind: 'text', optional: true },
    { key: 'expires_at', label: '만료일', kind: 'date', optional: true },
  ],
  checklist: [
    {
      key: 'recurrence', label: '반복 주기', kind: 'select',
      options: [
        { value: 'P1D', label: '매일' },
        { value: 'P1W', label: '매주' },
        { value: 'P1M', label: '매월' },
        { value: 'P3M', label: '분기마다' },
        { value: 'P6M', label: '반기마다' },
        { value: 'P1Y', label: '매년' },
      ],
    },
    { key: 'last_completed_at', label: '마지막 완료일', kind: 'date', optional: true },
    { key: 'notes_template', label: '점검 메모 템플릿', kind: 'multiline', optional: true },
  ],
};

/** 타입의 필드 정의 (알 수 없는 타입은 빈 배열) */
export function getItemFields(type: string): ItemFieldMeta[] {
  return ITEM_FIELDS[type as ItemType] ?? [];
}

/** payload 값 → 폼 입력용 문자열 */
export function toFormString(field: ItemFieldMeta, v: unknown): string {
  if (v === undefined || v === null) return '';
  if (field.kind === 'list' && Array.isArray(v)) return v.map(String).join(', ');
  if (field.kind === 'boolean') return v === true ? 'true' : v === false ? 'false' : '';
  if (field.kind === 'date' && typeof v === 'string') return v.slice(0, 10);
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

/** 상세 보기 표시용 문자열 (select·boolean은 한국어 레이블) */
export function toDisplayString(field: ItemFieldMeta, v: unknown): string {
  const s = toFormString(field, v);
  if (s === '') return '';
  if (field.kind === 'select') return field.options?.find((o) => o.value === s)?.label ?? s;
  if (field.kind === 'boolean') return s === 'true' ? '예' : '아니오';
  return s;
}

/**
 * 폼 문자열 → 서버 payload 값.
 * - empty=true: 비어 있음 (선택 필드면 키 제거, 필수 필드면 에러)
 * - error: 형식 오류 메시지
 */
export function fromFormString(
  field: ItemFieldMeta,
  raw: string,
): { empty: true } | { empty: false; value: unknown } | { empty: false; error: string } {
  const s = field.kind === 'secret' || field.kind === 'secret-multiline' ? raw : raw.trim();
  if (s.trim() === '') return { empty: true };

  switch (field.kind) {
    case 'number': {
      const n = Number(s);
      if (!Number.isFinite(n)) return { empty: false, error: `${field.label}은(는) 숫자여야 합니다` };
      if (n < 0) return { empty: false, error: `${field.label}은(는) 0 이상이어야 합니다` };
      // 포트·바이트는 정수, 금액은 소수 허용
      if (field.key !== 'amount' && !Number.isInteger(n)) {
        return { empty: false, error: `${field.label}은(는) 정수여야 합니다` };
      }
      if (field.key === 'port' && (n < 1 || n > 65535)) {
        return { empty: false, error: '포트는 1~65535 사이여야 합니다' };
      }
      return { empty: false, value: n };
    }
    case 'list':
      return { empty: false, value: s.split(',').map((x) => x.trim()).filter(Boolean) };
    case 'boolean':
      return { empty: false, value: s === 'true' };
    case 'select':
      if (field.options && !field.options.some((o) => o.value === s)) {
        return { empty: false, error: `${field.label} 값이 올바르지 않습니다` };
      }
      return { empty: false, value: s };
    case 'date':
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return { empty: false, error: `${field.label}은(는) YYYY-MM-DD 형식이어야 합니다` };
      return { empty: false, value: s };
    default:
      return { empty: false, value: s };
  }
}

/**
 * 폼 값 전체 → payload.
 * base(기존 payload)의 폼 밖 키는 보존하고, 비운 선택 필드는 키를 제거한다.
 * 반환: { payload } 또는 { errors: {key: message} }
 */
export function buildPayload(
  fields: ItemFieldMeta[],
  values: Record<string, string>,
  base: Record<string, unknown> = {},
): { payload: Record<string, unknown>; errors: Record<string, string> } {
  const payload: Record<string, unknown> = { ...base };
  const errors: Record<string, string> = {};
  for (const f of fields) {
    const r = fromFormString(f, values[f.key] ?? '');
    if (r.empty) {
      if (!f.optional) errors[f.key] = `${f.label}은(는) 필수입니다`;
      delete payload[f.key];
    } else if ('error' in r) {
      errors[f.key] = r.error;
    } else {
      payload[f.key] = r.value;
    }
  }
  return { payload, errors };
}
